import { Link, useNavigate } from '@tanstack/react-router';
import { CircleArrowLeft } from 'lucide-react';
import { parse, unparse } from 'papaparse';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetBulkImportClassesStatus } from '~entities/classes/api/get-bulk-import-status';
import { useBulkUploadClassesMutation } from '~entities/classes/api/upload-class-csv';
import { useGetHourlyTuitionBilling } from '~entities/payment/api/hourly-tuition/get-hourly-tuition-billing';

import { BulkImportClassesProgressModal } from './bulk-import-progress-modal';
import { ColumnMapping } from './column-mapping';
import { FileSelector } from './file-selector';
import type { ClassColumnMappingTableData, ClassCSVHeader } from './types';
import { ValidateCSV } from './validate-csv';
import type { ClassData } from './validation';

const TABLE_DATA: Array<ClassColumnMappingTableData> = [
  {
    header: 'Title',
    sample: 'Twinkle Star Kids',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Class Color',
    sample: '#E91E63',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Hide Class Status',
    sample: 'no',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Location',
    sample: 'Ballet Dance Academy',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Room',
    sample: 'Ballet Room',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'start age year',
    sample: '3',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'start age month',
    sample: '5',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'end age year',
    sample: '7',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'end age month',
    sample: '0',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Instructor',
    sample: 'Lily Grace',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Description (Under 200 Characters)',
    sample:
      'A fun introduction to dance for little ones, focusing on rhythm, movement, and coordination.',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Session',
    sample: '2024-25',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Start Date',
    sample: '12/29/2024',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'End Date',
    sample: '5/31/2025',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Registration Start Date',
    sample: '12/11/2024',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Days',
    sample: 'Mon, Wed, Fri',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Start Time',
    sample: '10:00 AM',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'End Time',
    sample: '11:30 AM',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Registration Fee',
    sample: '50',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Tuition Fee',
    sample: '500',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Costume Fee',
    sample: '50',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Class Size',
    sample: '200',
    selectedColumn: '',

    columnValue: '',
  },
  {
    header: 'Group',
    sample: 'Ballet',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Tuition Billing Cycle',
    sample: 'Monthly',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Billing Frequency Days',
    sample: '14',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Hourly Plan Name',
    sample: 'Standard Plan',
    selectedColumn: '',
    columnValue: '',
  },
  {
    header: 'Billing Day',
    sample: '1',
    selectedColumn: '',
    columnValue: '',
  },
];

export function BulkUploadClasses() {
  const navigate = useNavigate();
  const mutation = useBulkUploadClassesMutation();
  const { data: hourlyPlans } = useGetHourlyTuitionBilling();

  const [step, setStep] = useState<'select' | 'mapping' | 'validate'>('select');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCSVData] = useState<Array<ClassData>>([]);
  const [headers, setHeaders] = useState<Array<string>>([]);
  const [columnMappingTableData, setColumnMappingTableData] = useState<
    Array<ClassColumnMappingTableData>
  >([]);

  // Extract plan names from hourly plans (case-sensitive)
  const hourlyPlanNames = hourlyPlans?.map((plan) => plan.name) ?? [];

  const [showBulkImportProgressModal, setShowBulkImportProgressModal] =
    useState(false);
  const [bulkImportJobId, setBulkImportJobId] = useState<string | null>(null);
  const [bulkImportInitialData, setBulkImportInitialData] = useState<{
    totalRecords: number;
    validationErrors: Array<{
      record: Record<string, string | number | undefined>;
      error: string;
    }>;
    estimatedTime?: string;
  } | null>(null);

  const { data: bulkImportJobStatus } = useGetBulkImportClassesStatus({
    jobId: bulkImportJobId ?? '',
  });

  const job = bulkImportJobStatus?.job;
  const jobStatus = job?.status;
  const isBulkImportProcessing =
    jobStatus === 'queued' || jobStatus === 'processing';

  useEffect(() => {
    if (!isBulkImportProcessing) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBulkImportProcessing]);

  const handleUpload = useCallback(async () => {
    if (!file) return;

    const csv = unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    try {
      const result = await mutation.mutateAsync(blob as File);
      const response = result;

      setBulkImportJobId(response.jobId);
      setBulkImportInitialData({
        totalRecords: response.totalRecords,
        validationErrors: response.validationErrors,
        estimatedTime: response.estimatedTime,
      });
      setShowBulkImportProgressModal(true);

      if (response.validationErrors.length > 0) {
        toast.warning(
          `${response.validationErrors.length} validation error(s) found. Processing will continue.`,
        );
      } else {
        toast.success('Bulk import started.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error starting bulk import');
    }
  }, [csvData, file, mutation]);

  const onFileChange = useCallback((file: File | null) => {
    if (!file) return;
    setFile(file);
    parse<ClassData>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsedCSVData = result.data;
        const parsedHeaders = result.meta.fields ?? [];

        const getColumnValue = (header?: ClassCSVHeader) => {
          const csvRow = parsedCSVData[0];
          if (!header || !csvRow) return '';
          return csvRow[header as keyof ClassData];
        };

        const tableRows = TABLE_DATA.map((row) => {
          const columnHeader = parsedHeaders.find(
            (header) => header === row.header,
          ) as ClassCSVHeader | undefined;
          return {
            ...row,
            selectedColumn: columnHeader ?? '',
            columnValue: getColumnValue(columnHeader),
          };
        });

        setCSVData(parsedCSVData);
        setHeaders(parsedHeaders);
        setColumnMappingTableData(tableRows);
      },
    });
  }, []);

  const onNext = useCallback(() => {
    if (!file) return;
    setStep('mapping');
  }, [file]);

  return (
    <div className='flex flex-col'>
      <div className='w-full p-2 pl-1'>
        <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <span className='text-lg font-semibold'>Back to Dashboard</span>
        </Link>
      </div>
      {step === 'select' && (
        <FileSelector file={file} setFile={onFileChange} onNext={onNext} />
      )}
      {step === 'mapping' && (
        <ColumnMapping
          columnHeaders={headers}
          csvData={csvData}
          setCSVData={setCSVData}
          setStep={setStep}
          columnMappingTableData={columnMappingTableData}
          setColumnMappingTableData={setColumnMappingTableData}
        />
      )}
      {step === 'validate' && (
        <ValidateCSV
          columnHeaders={headers}
          csvData={csvData}
          setStep={setStep}
          handleUpload={handleUpload}
          isUploading={mutation.isPending}
          hourlyPlanNames={hourlyPlanNames}
        />
      )}

      <BulkImportClassesProgressModal
        jobId={bulkImportJobId}
        open={showBulkImportProgressModal}
        onOpenChange={(open) => {
          setShowBulkImportProgressModal(open);
          if (!open) {
            setBulkImportJobId(null);
            setBulkImportInitialData(null);
          }
        }}
        initialData={bulkImportInitialData ?? undefined}
        onComplete={() => {
          void navigate({ to: '/classes' });
          setFile(null);
        }}
      />
    </div>
  );
}
